import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import OutlinedInput from '@mui/material/OutlinedInput';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Slider } from '@mui/material';
import collegeServices from 'services/collegeServices';

/**
 * SPEC_College_Search_Filters (v1.1) — Location modal (Lovable taxonomy).
 * States filter is backed by `state_codes`. Distance is UI-only (no backend yet).
 */

type StateRow = { descr: string; id: number; state_code: string; state_i: string };

const toggle = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

export default function ModalLocation({ open, setOpen, formik }: any) {
  const [states, setStates] = React.useState<StateRow[]>([]);
  const [keyword, setKeyword] = React.useState('');
  const [selectedStates, setSelectedStates] = React.useState<string[]>([]);
  // UI-only: no backend support / home address yet.
  const [distance, setDistance] = React.useState<number[]>([0, 3000]);

  React.useEffect(() => {
    collegeServices.getState().then((res: any) => setStates(res ?? []));
  }, []);

  // Sync selections from persisted formik filters whenever the modal opens.
  React.useEffect(() => {
    if (!open) return;
    setSelectedStates(formik.values.state_codes ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filteredStates = (states ?? []).filter(
    (i) =>
      i.descr.toLowerCase().includes(keyword.toLowerCase()) ||
      i.state_code.toLowerCase().includes(keyword.toLowerCase()),
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} keepMounted fullWidth maxWidth="sm">
      <DialogTitle className="fw-bold fs-3">Location</DialogTitle>
      <DialogContent>
        <Box className="fw-bold fs-5 mt-2">States</Box>
        <OutlinedInput
          margin="dense"
          placeholder="Search states…"
          fullWidth
          size="small"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Box
          sx={{
            maxHeight: '32dvh',
            overflow: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
          }}
        >
          {filteredStates.map((item) => (
            <FormControlLabel
              key={item.state_code}
              className="m-0"
              control={
                <Checkbox
                  size="small"
                  checked={selectedStates.includes(item.state_code)}
                  onChange={() => setSelectedStates((p) => toggle(p, item.state_code))}
                />
              }
              label={<span style={{ fontSize: 13 }}>{item.state_code}</span>}
              title={item.descr}
            />
          ))}
        </Box>

        <Box className="fw-bold fs-5 mt-3">
          Distance from home: {distance[0]} – {distance[1]} mi
        </Box>
        <Box className="m-3">
          <Slider
            min={0}
            max={3000}
            step={50}
            value={distance}
            onChange={(_e, nv) => setDistance(nv as number[])}
            valueLabelDisplay="auto"
            marks={[
              { value: 0, label: '0' },
              { value: 3000, label: '3000' },
            ]}
          />
          <Box sx={{ fontSize: 11, color: '#6B7280' }}>Based on your profile home address.</Box>
        </Box>
      </DialogContent>
      <DialogActions className="p-4">
        <Button
          variant="contained"
          onClick={() => {
            formik.setFieldValue('state_codes', selectedStates);
            formik.handleSubmit();
            setOpen(false);
          }}
        >
          Show Colleges
        </Button>
      </DialogActions>
    </Dialog>
  );
}
